import React from 'react';
import { Form, Row, Col, Button } from 'react-bootstrap';

import useFileUpload from 'hooks/useFileUpload';
import FileUploader from 'components/common/FileUploader';
import useFormValidation from 'hooks/useFormValidation';

import { COUNTRY_LIST, STATE_LIST } from 'constants/address';
import { WAREHOUSE_VALIDATION_SCHEMA } from 'constants/admin/warehouse';

const WarehouseEditForm = ({ warehouse, onSubmit, onCancel, isLoading }) => {
  const initialValues = {
    name: warehouse?.name || '',
    party_name: warehouse?.party_name || '',
    inventory_available: warehouse?.inventory_available,
    address_name: warehouse?.address_name || '',
    address_line_1: warehouse?.address_line_1 || '',
    address_line_2: warehouse?.address_line_2 || '',
    address_city: warehouse?.address_city || '',
    address_state: warehouse?.address_state || '',
    address_country: warehouse?.address_country || '',
    address_pincode: warehouse?.address_pincode || '',
    address_email: warehouse?.address_email || '',
    address_phone: warehouse?.address_phone || '',
    gstin: warehouse?.gstin || '',
  };

  const logoUpload = useFileUpload({
    fieldId: 'logo_image',
    initialFile: warehouse?.logo_image,
  });

  const signatureUpload = useFileUpload({
    fieldId: 'signature_image',
    initialFile: warehouse?.signature_image,
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateForm,
    isValid,
  } = useFormValidation(initialValues, WAREHOUSE_VALIDATION_SCHEMA);

  const handleSubmit = e => {
    e.preventDefault();

    const imageStates = {
      logo_image: {
        file: logoUpload.file,
        isRemoved: logoUpload.isRemoved,
        hasChanged: logoUpload.hasChanged,
      },
      signature_image: {
        file: signatureUpload.file,
        isRemoved: signatureUpload.isRemoved,
        hasChanged: signatureUpload.hasChanged,
      },
    };

    onSubmit(e, imageStates);
  };

  const handleCancel = () => {
    logoUpload.clearState();
    signatureUpload.clearState();

    onCancel();
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={values.name}
              onChange={e => handleChange('name', e.target.value)}
              onBlur={() => handleBlur('name')}
              isInvalid={touched.name && errors.name}
            />
            <Form.Control.Feedback type="invalid">
              {errors.name}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Party Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="party_name"
              value={values.party_name}
              onChange={e => handleChange('party_name', e.target.value)}
              onBlur={() => handleBlur('party_name')}
              isInvalid={touched.party_name && errors.party_name}
            />
            <Form.Control.Feedback type="invalid">
              {errors.party_name}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Status <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              name="inventory_available"
              value={values.inventory_available}
              onChange={e =>
                handleChange('inventory_available', e.target.value)
              }
              onBlur={() => handleBlur('inventory_available')}
              isInvalid={
                touched.inventory_available && errors.inventory_available
              }
            >
              <option value="true">Available</option>
              <option value="false">Unavailable</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.inventory_available}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Logo Image</Form.Label>
            <FileUploader
              file={logoUpload.file}
              initialFile={logoUpload.initialFile}
              getRootProps={logoUpload.getRootProps}
              getInputProps={logoUpload.getInputProps}
              resetFile={logoUpload.resetFile}
              isRemoved={logoUpload.isRemoved}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Signature Image</Form.Label>
            <FileUploader
              file={signatureUpload.file}
              initialFile={signatureUpload.initialFile}
              getRootProps={signatureUpload.getRootProps}
              getInputProps={signatureUpload.getInputProps}
              resetFile={signatureUpload.resetFile}
              isRemoved={signatureUpload.isRemoved}
            />
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Authorised Person</Form.Label>
            <Form.Control
              type="text"
              name="address_name"
              value={values.address_name}
              onChange={e => handleChange('address_name', e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Address Line 1</Form.Label>
            <Form.Control
              type="text"
              name="address_line_1"
              value={values.address_line_1}
              onChange={e => handleChange('address_line_1', e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Address Line 2</Form.Label>
            <Form.Control
              type="text"
              name="address_line_2"
              value={values.address_line_2}
              onChange={e => handleChange('address_line_2', e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>City</Form.Label>
            <Form.Control
              type="text"
              name="address_city"
              value={values.address_city}
              onChange={e => handleChange('address_city', e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>State</Form.Label>
            <Form.Select
              name="address_state"
              value={values.address_state}
              onChange={e => handleChange('address_state', e.target.value)}
            >
              <option value="">Select State...</option>
              {STATE_LIST.map(state => (
                <option key={state.value} value={state.value}>
                  {state.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Country</Form.Label>
            <Form.Select
              name="address_country"
              value={values.address_country}
              onChange={e => handleChange('address_country', e.target.value)}
            >
              <option value="">Select Country...</option>
              {COUNTRY_LIST.map(country => (
                <option key={country.value} value={country.value}>
                  {country.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Pincode</Form.Label>
            <Form.Control
              type="text"
              name="address_pincode"
              value={values.address_pincode}
              onChange={e => handleChange('address_pincode', e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              name="address_email"
              value={values.address_email}
              onChange={e => handleChange('address_email', e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Phone</Form.Label>
            <Form.Control
              type="text"
              name="address_phone"
              value={values.address_phone}
              onChange={e => handleChange('address_phone', e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              GSTIN <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="gstin"
              value={values.gstin}
              onChange={e => handleChange('gstin', e.target.value)}
              onBlur={() => handleBlur('gstin')}
              isInvalid={touched.gstin && errors.gstin}
            />
            <Form.Control.Feedback type="invalid">
              {errors.gstin}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <div className="d-flex justify-content-end gap-2 mt-4">
        <Button variant="secondary" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="success"
          disabled={!isValid || isLoading}
        >
          {isLoading ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </Form>
  );
};

export default WarehouseEditForm;
